import type { PostHog, Properties } from 'posthog-js';
import { POSTHOG_KEY, POSTHOG_HOST, POSTHOG_DEBUG } from '../config';
import { leerIdsDeVisita, type IdsDeVisita } from './posthogEntreNavegadores';

// Cliente singleton de PostHog (product analytics + session replay + error
// tracking). Ver ADR-2026-07-31-posthog-analytics.
//
// TRES REGLAS QUE EXPLICAN LA FORMA DE ESTE ARCHIVO:
//
// 1. NADIE fuera de acá importa `posthog-js`. El SDK pesa ~200 KB y se carga
//    con `import()` dinámico, así que Vite lo deja en un chunk aparte que baja
//    DESPUÉS del primer render. Si otro módulo lo importara arriba de todo, el
//    bundler lo volvería a meter en el bundle principal y perderíamos eso.
// 2. Todo pasa por `conPostHog`: si el SDK todavía no cargó, si el usuario lo
//    tiene bloqueado por un adblocker o si estamos en dev, las llamadas no
//    hacen nada. Analytics NUNCA puede romper la app.
// 3. Solo trackea en producción. En localhost no se manda nada, salvo que
//    pongas VITE_POSTHOG_DEBUG=true para probar la integración misma.

let cargando: Promise<PostHog | null> | null = null;

// Copia local de las super properties (propiedades que el SDK pega a TODOS los
// eventos). La guardamos porque `reset()` las borra: sin esto, desloguearse
// dejaría el resto de la sesión sin `pwa_standalone` y compañía.
let superProps: Properties = {};

// Propiedades de persona pendientes de mandar en el próximo `identify`.
// Ver `fijarPropsDePersonaUnaVez`.
let propsPersonaUnaVez: Properties | null = null;

function habilitado(): boolean {
  if (!POSTHOG_KEY) return false;
  return import.meta.env.PROD || POSTHOG_DEBUG;
}

/**
 * @param superPropsIniciales Propiedades que se pegan a TODOS los eventos.
 *   Se registran dentro del callback `loaded`, que corre ANTES del `$pageview`
 *   inicial: si se registraran desde afuera (después del `await` del import
 *   dinámico) ese primer pageview saldría sin ellas.
 */
export function initPostHog(superPropsIniciales?: Properties): Promise<PostHog | null> {
  if (cargando) return cargando;

  // Antes del primer render y del pageview: si la visita viene de Instagram (el link trae
  // `ph_did`), Chrome sigue siendo la misma persona. Los ids se sacan de la URL siempre.
  const llegada = idsQueTrajoElLink();

  if (!habilitado()) {
    cargando = Promise.resolve(null);
    return cargando;
  }

  if (superPropsIniciales) superProps = { ...superProps, ...superPropsIniciales };

  cargando = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        loaded: (ph) => {
          if (Object.keys(superProps).length > 0) ph.register(superProps);
        },
        api_host: POSTHOG_HOST,
        // Tanda de defaults más nueva que soporta el SDK instalado. Nos importa
        // sobre todo `capture_pageview: 'history_change'`: los pageviews de
        // react-router salen solos, sin hook de ruteo. También activa
        // `canvasCapture`, sin lo cual el editor de imágenes/video se ve como un
        // rectángulo vacío en los replays (hay que habilitarlo ADEMÁS en el
        // panel: Session replay → Capture canvas elements).
        defaults: '2026-06-25',
        // No creamos perfil de persona para visitantes anónimos: solo para los
        // que pasan por `identifyUser`. Los anónimos igual generan eventos.
        person_profiles: 'identified_only',
        // Error tracking: errores no manejados + promesas rechazadas.
        capture_exceptions: true,
        session_recording: {
          // Se enmascaran los campos que el usuario tipea; el resto de la UI
          // (imágenes generadas, textos, clicks) se ve. Es lo único que hace
          // útil el replay en una app visual.
          maskAllInputs: true,
          maskInputOptions: { password: true },
        },
        debug: POSTHOG_DEBUG,
        // Con `ph_did` en el link (ver `idsQueTrajoElLink`). Si este navegador ya tenía
        // identidad guardada, PostHog se queda con la suya: el bootstrap no la pisa.
        ...(llegada
          ? { bootstrap: { distinctID: llegada.distinctId, sessionID: llegada.sessionId ?? undefined } }
          : {}),
      });
      return posthog;
    })
    .catch(() => null);

  return cargando;
}

/**
 * Los ids de PostHog que trajo el link desde el navegador de Instagram, y los saca de la
 * URL (con `replaceState`: todavía no montó React Router). Ver `posthogEntreNavegadores.ts`.
 */
function idsQueTrajoElLink(): IdsDeVisita | null {
  if (typeof window === 'undefined') return null;
  const { ids, sinIds } = leerIdsDeVisita(window.location.href);
  if (sinIds !== window.location.href) {
    try {
      window.history.replaceState(window.history.state, '', sinIds);
    } catch {
      // Si no se puede reescribir, los ids quedan en la barra: no rompe nada.
    }
  }
  return ids;
}

/**
 * Los ids de esta visita, para pegarlos en el link al navegador. `null` si PostHog no cargó
 * (adblocker, dev) o tarda más de `esperaMaxMs`: el salto no espera a la analítica.
 */
export function idsDeEstaVisita(esperaMaxMs: number): Promise<IdsDeVisita | null> {
  const ids = (cargando ?? initPostHog())
    .then((ph) => {
      if (!ph) return null;
      try {
        return { distinctId: ph.get_distinct_id(), sessionId: ph.get_session_id() || null };
      } catch {
        return null;
      }
    })
    .catch(() => null);
  const tope = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), esperaMaxMs));
  return Promise.race([ids, tope]);
}

function conPostHog(fn: (ph: PostHog) => void): void {
  (cargando ?? initPostHog())
    .then((ph) => {
      if (!ph) return;
      try {
        fn(ph);
      } catch {}
    })
    .catch(() => {});
}

/**
 * Asocia la sesión actual con el usuario de Supabase. Usamos el UUID de auth
 * como `distinct_id` para que sea el mismo id que ves en Supabase y en las
 * tablas de negocio.
 */
export function identifyUser(userId: string, email?: string | null): void {
  conPostHog((ph) => {
    ph.identify(
      userId,
      email ? { email } : undefined,
      propsPersonaUnaVez ?? undefined,
    );
  });
}

/**
 * Deja propiedades de persona listas para viajar con el **próximo** `identify`
 * (como `$set_once`: se escriben una sola vez y no se pisan).
 *
 * No se mandan en el momento a propósito: con `person_profiles:
 * 'identified_only'` (ADR-2026-07-31-posthog-analytics) un `setPersonProperties`
 * sobre un anónimo le crearía perfil, que es justo lo que se decidió evitar. Y
 * quien llama a esto corre en el arranque, mucho antes del login.
 */
export function fijarPropsDePersonaUnaVez(props: Properties): void {
  propsPersonaUnaVez = { ...(propsPersonaUnaVez ?? {}), ...props };
}

/**
 * Agrupa al usuario bajo su empresa. Habilita filtrar cualquier embudo, gráfico
 * o replay por plan / país / cuenta de prueba desde el panel — sin tener que
 * tocar código para excluir a los dev_tester.
 */
export function identifyEmpresa(idCompany: string, props?: Properties): void {
  conPostHog((ph) => {
    ph.group('empresa', idCompany, props);
  });
}

/** Corta el vínculo usuario ↔ sesión al desloguear (evita mezclar cuentas). */
export function resetPostHog(): void {
  conPostHog((ph) => {
    ph.reset();
    // `reset()` también limpia las super properties, que describen el entorno
    // (no al usuario) y siguen valiendo después del logout: las reponemos.
    if (Object.keys(superProps).length > 0) ph.register(superProps);
  });
}

/** Error manejado (el autocapture solo ve los que llegan a `window`). */
export function captureException(error: unknown, props?: Properties): void {
  conPostHog((ph) => {
    ph.captureException(error, props);
  });
}

/** Evento de negocio. El autocapture ya cubre clicks y pageviews. */
export function trackEvent(evento: string, props?: Properties): void {
  conPostHog((ph) => {
    ph.capture(evento, props);
  });
}
